import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAppData } from '../hooks/useAppData';
import { Lock, User, Check, ArrowRight } from 'lucide-react';
import { getErrorMessage } from '../utils/errors';

const ChangeCredentials: React.FC = () => {
    const { profile } = useAppData();
    const [newUsername, setNewUsername] = useState(profile?.username || '');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // בדיקות תקינות (נשאר אותו דבר)
        if (newPassword.length < 6) { setError('הסיסמה חייבת להכיל 6 תווים לפחות.'); return; }
        if (newPassword !== confirmPassword) { setError('הסיסמאות אינן תואמות.'); return; }
        if (newUsername.length < 3) { setError('שם המשתמש קצר מדי.'); return; }
        if (!/^[a-zA-Z0-9_]+$/.test(newUsername)) { setError('שם המשתמש חייב להכיל אנגלית ומספרים בלבד.'); return; }

        setLoading(true);

        try {
            const newFakeEmail = `${newUsername}@remind.com`;

            // 1. עדכון סיסמה (זה עובד רגיל)
            const { error: passError } = await supabase.auth.updateUser({
                password: newPassword
            });
            if (passError) throw passError;

            // 2. עדכון שם משתמש (באמצעות הפונקציה העוקפת שיצרנו ב-SQL)
            // זה פותר את בעיית האימות התקוע!
            const { error: emailError } = await supabase.rpc('update_email_bypass', {
                new_email: newFakeEmail
            });

            if (emailError) throw emailError;

            // 3. עדכון הפרופיל בטבלה שלנו
            // שים לב: וודא שהשמות של העמודות (username/first_login) תואמים למה שיש לך ב-DB
            // אם ב-SQL הקודם השתמשנו ב-full_name ו-is_temp_password, תשנה בהתאם:
            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                    username: newUsername,
                    first_login: false
                })
                .eq('id', profile?.id);

            if (profileError) throw profileError;

            alert("הפרטים עודכנו בהצלחה! מתחברים מחדש...");
            window.location.reload();

        } catch (err) {
            console.error(err);
            const message = getErrorMessage(err);
            if (message.includes('unique constraint') || message.includes('duplicate')) {
                setError('שם המשתמש הזה כבר תפוס.');
            } else {
                setError('שגיאה בעדכון: ' + message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4 font-sans" dir="rtl">
            <div className="w-full max-w-md bg-card p-8 rounded-3xl border border-border shadow-sm animate-fade-in relative overflow-hidden">

                <header className="text-center mb-8 relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/20 rotate-3">
                        <Lock size={28} className="text-white drop-shadow-md" />
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2 tracking-tight">ברוכים הבאים ל-ReMind!</h1>
                    <p className="text-muted-foreground text-sm max-w-xs mx-auto leading-relaxed">
                        לפני שמתחילים, בואו נדאג שהחשבון שלכם יהיה מוגן ואישי.
                    </p>
                </header>

                <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                    <div>
                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">שם משתמש חדש</label>
                        <div className="relative group">
                            <User className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
                            <input
                                type="text"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                className="w-full bg-muted/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-muted focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="למשל: גיבור_העל_123"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">סיסמה חדשה</label>
                        <div className="relative group">
                            <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full bg-muted/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-muted focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="לפחות 6 תווים"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">אימות סיסמה</label>
                        <div className="relative group">
                            <Check className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-green-500 transition-colors" size={20} />
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full bg-muted/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-muted focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="הקלד שוב את הסיסמה"
                                required
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 text-red-400 text-sm p-4 rounded-xl border border-red-500/20 text-center animate-shake">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 mt-6 shadow-lg shadow-primary/25 active:scale-[0.98]"
                    >
                        {loading ? 'מעדכן פרטים...' : (
                            <>
                                צאו לדרך! <ArrowRight size={20} className="rotate-180" />
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChangeCredentials;
